import {
  addGeneralMeetingDocuments,
  DecisionContext,
  DbotEventDispatcher,
} from "../common/commonDomain";
import { makeAutoObservable } from "mobx";
import { ExtendedShareholderDTO } from "../../types/Shareholder";
import { when } from "../../utils/functions";
import { CURRENCY_NOK } from "../../utils/currency";
import { PaymentMethods } from "../../types/Transactions";
import { TMessage } from "../../i18n/tt";
import { DecisionType } from "../../common/decisions";
import {
  BooleanFieldModel,
  LocalDateFieldModel,
  NumberFieldModel,
  RadioFieldModel,
} from "../../common/commonForms";
import { ShareType, ShareTypeCode, shareTypeLabel } from "../../common/share";

export type Dividend = {
  type: DecisionType.dividend;
  isExtraordinaryDividend: boolean;
  extraordinaryDividendDate?: string;
  isBalanceSheetApproved: boolean;
  extraordinaryDividendGmApprovalDate?: string;
  extraordinaryDividendRevised?: boolean;
  usePowerOfAttorney?: boolean;
  powerOfAttorneyDate?: string;
  isDividendEvenlyDivided: boolean;
  isDividendPeriodNormal: boolean;
  dividendRepaymentOfCapital: boolean;
  resolutionBy: string;
  shareholderEvenDividends: EvenDividend[];
  shareholderUnevenDividends: UnevenDividend[];
  dividendDate?: string;
};

type EvenDividend = {
  id: number;
  name: string;
  value?: number;
  isValueTotal?: boolean;
  isCompanySharesExcluded?: boolean;
};

class EvenDividendModel {
  id: number;
  name: string;
  value: NumberFieldModel;
  dividendType: RadioFieldModel<PaymentMethods>;

  constructor(
    id: number,
    name: string,
    value?: number,
    isValueTotal?: boolean,
    isCompanySharesExcluded?: boolean,
  ) {
    this.id = id;
    this.name = name;
    this.value = new NumberFieldModel({ value: value, required: true, isPositiveOrZero: true });
    this.dividendType = new RadioFieldModel<PaymentMethods>({
      value: this.toDividendType(isCompanySharesExcluded, isValueTotal),
      options: [PaymentMethods.PER_SHARE, PaymentMethods.TOTAL_EX, PaymentMethods.TOTAL_INCL],
      idFn: (v) => v ?? "perShare",
      labelFn: (v) =>
        v
          ? { key: `decisions.dividend.evenDividendType.${v}` }
          : { key: "decisions.dividend.evenDividendType.perShare" },
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  toDividendType(isCompanySharesExcluded?: boolean, isValueTotal?: boolean) {
    if (isValueTotal && isCompanySharesExcluded) {
      return PaymentMethods.TOTAL_EX;
    } else if (isValueTotal && !isCompanySharesExcluded) {
      return PaymentMethods.TOTAL_INCL;
    }
    return PaymentMethods.PER_SHARE;
  }

  validate() {
    return this.value.validate();
  }

  get isValid() {
    return this.value.isValid;
  }
}

type ShareTypeDividend = {
  id: number;
  name: string;
  value: number;
};

class ShareTypeDividendModel {
  id: number;
  name: string;
  value: NumberFieldModel;

  constructor(id: number, name: string, value: number | undefined) {
    this.id = id;
    this.name = name;
    this.value = new NumberFieldModel({ value: value, isPositiveOrZero: true });
  }

  validate() {
    this.value.validate();
  }

  get isValid() {
    return this.value.isValid;
  }
}

type UnevenDividend = {
  id: number;
  name: string;
  shareType: ShareTypeDividend[];
};

class UnevenDividendModel {
  id: number;
  name: string;
  shareType: ShareTypeDividendModel[];

  constructor(id: number, name: string, shareType: ShareTypeDividend[]) {
    this.id = id;
    this.name = name;
    this.shareType = shareType.map((s) => new ShareTypeDividendModel(s.id, s.name, s.value));
    makeAutoObservable(this);
  }

  validate() {
    this.shareType.forEach((s) => s.validate());
  }

  get isValid() {
    return this.shareType.every((s) => s.isValid);
  }
}

export class DividendForm {
  readonly type = DecisionType.dividend;
  dispatch: DbotEventDispatcher;
  context: DecisionContext;
  data?: Dividend;
  isExtraordinaryDividend: BooleanFieldModel;
  extraordinaryDividendDate: LocalDateFieldModel;
  extraordinaryDividendRevised: BooleanFieldModel;
  usePowerOfAttorney: BooleanFieldModel;
  isDividendEvenlyDivided: BooleanFieldModel;
  shareholderEvenDividends: EvenDividendModel[];
  shareholderUnevenDividends: UnevenDividendModel[];
  dividendRepaymentOfCapital: BooleanFieldModel;
  isDividendPeriodNormal: RadioFieldModel<boolean>;
  dividendDate: LocalDateFieldModel;
  powerOfAttorneyDate: LocalDateFieldModel;
  isBalanceSheetApproved: RadioFieldModel<boolean>;
  extraordinaryDividendGmApprovalDate: LocalDateFieldModel;
  validEvenDividendShareTypes = true;
  validUnevenShareholdersSelected = true;

  constructor(dispatch: DbotEventDispatcher, context: DecisionContext, data?: Dividend) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.dispatch = dispatch;
    this.context = context;
    this.data = data;

    this.isExtraordinaryDividend = new BooleanFieldModel({
      value: data?.isExtraordinaryDividend,
      required: true,
      onChange: this.updateRequiredDependencies,
    });
    this.extraordinaryDividendDate = new LocalDateFieldModel({
      value: data?.extraordinaryDividendDate,
    });
    this.isBalanceSheetApproved = new RadioFieldModel<boolean>({
      value: data?.isBalanceSheetApproved ?? true,
      onChange: this.updateRequiredDependencies,
      options: [true, false],
      idFn: (v) => String(v),
      labelFn: (v) =>
        v
          ? { key: `decisions.dividend.balanceSheet.gmApproval.true` }
          : { key: `decisions.dividend.balanceSheet.gmApproval.false` },
    });
    this.extraordinaryDividendGmApprovalDate = new LocalDateFieldModel({
      value: data?.extraordinaryDividendGmApprovalDate,
    });
    this.extraordinaryDividendRevised = new BooleanFieldModel({
      value: data?.extraordinaryDividendRevised,
      onChange: this.updateRequiredDependencies,
    });

    this.usePowerOfAttorney = new BooleanFieldModel({
      value: data?.usePowerOfAttorney,
      required: true,
      onChange: this.updateRequiredDependencies,
    });
    this.powerOfAttorneyDate = new LocalDateFieldModel({
      value: data?.powerOfAttorneyDate,
    });

    this.isDividendEvenlyDivided = new BooleanFieldModel({
      value: data?.isDividendEvenlyDivided,
      required: true,
    });
    this.shareholderEvenDividends = this.initialiseShareholderEvenDividends();
    this.shareholderUnevenDividends =
      data?.shareholderUnevenDividends.map(
        (d) => new UnevenDividendModel(d.id, d.name, d.shareType),
      ) ?? [];
    this.dividendRepaymentOfCapital = new BooleanFieldModel({
      value: data?.dividendRepaymentOfCapital ?? false,
      required: true,
    });
    this.isDividendPeriodNormal = new RadioFieldModel<boolean>({
      value: data?.isDividendPeriodNormal ?? true,
      options: [true, false],
      idFn: (v) => String(v),
      labelFn: (v) => ({ key: `decisions.dividend.dividendPeriodNormal.${v}` }),
    });
    this.dividendDate = new LocalDateFieldModel({
      value: data?.dividendDate,
      required: !this.isDividendPeriodNormal.value,
    });
    this.updateRequiredDependencies();
  }

  initialiseShareholderEvenDividends(): EvenDividendModel[] {
    return this.shareTypes.map((s) => {
      const existingEvenDividend: EvenDividend | undefined = this.getExistingDividend(
        s.sharetypeId,
      );
      const amount = existingEvenDividend?.value;
      const isValueTotal = existingEvenDividend?.isValueTotal;
      const isCompanySharesExcluded = existingEvenDividend?.isCompanySharesExcluded;
      return new EvenDividendModel(
        s.sharetypeId,
        s.name,
        amount,
        isValueTotal,
        isCompanySharesExcluded,
      );
    });
  }

  updateRequiredDependencies() {
    this.extraordinaryDividendDate.setRequired(this.isExtraordinaryDividend.value);
    this.extraordinaryDividendRevised.setRequired(Boolean(this.isExtraordinaryDividend.value));
    this.extraordinaryDividendGmApprovalDate.setRequired(this.isBalanceSheetApproved.value);
    this.powerOfAttorneyDate.setRequired(this.usePowerOfAttorney.value);
  }

  get shareTypes(): ShareType[] {
    return this.context.companyInfo.shareTypes;
  }

  get shareholders() {
    return this.context.shareholderData.shareholders;
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid(): boolean {
    const extraordinaryGMOk =
      !this.isExtraordinaryDividend.value ||
      !this.isBalanceSheetApproved.value ||
      this.extraordinaryDividendGmApprovalDate.isValid;
    return (
      this.isExtraordinaryDividend.isValid &&
      this.extraordinaryDividendDate.isValid &&
      this.extraordinaryDividendRevised.isValid &&
      extraordinaryGMOk &&
      this.usePowerOfAttorney.isValid &&
      this.isDividendEvenlyDivided.isValid &&
      this.dividendRepaymentOfCapital.isValid &&
      this.dividendDate.isValid &&
      this.powerOfAttorneyDate.isValid &&
      (this.isDividendEvenlyDivided.value
        ? this.shareholderEvenDividends.every((s) => s.isValid)
        : this.shareholderUnevenDividends.every((s) => s.isValid)) &&
      (this.isDividendEvenlyDivided.value
        ? this.validEvenDividendShareTypes
        : this.validUnevenShareholdersSelected)
    );
  }

  validate(): boolean {
    this.isExtraordinaryDividend.validate();
    this.extraordinaryDividendDate.validate();
    this.extraordinaryDividendRevised.validate();
    this.extraordinaryDividendGmApprovalDate.validate();
    this.usePowerOfAttorney.validate();
    this.isDividendEvenlyDivided.validate();
    this.dividendRepaymentOfCapital.validate();
    this.dividendDate.validate();
    this.powerOfAttorneyDate.validate();
    this.isDividendEvenlyDivided.validate();
    this.shareholderEvenDividends.forEach((s) => s.validate());
    if (this.isDividendEvenlyDivided.value === true) {
      this.validateEvenDividendHasSelectedShareTypes();
    }
    if (this.isDividendEvenlyDivided.value === false) {
      this.validateUnevenShareholdersSelected();
    }
    this.shareholderUnevenDividends.forEach((s) => s.validate());
    return this.isValid;
  }

  private validateEvenDividendHasSelectedShareTypes() {
    if (!this.hasSelectedShareTypes()) {
      this.validEvenDividendShareTypes = false;
    }
  }

  private validateUnevenShareholdersSelected() {
    if (!this.hasSelectedShareholders()) {
      this.validUnevenShareholdersSelected = false;
    }
  }

  hasSelectedShareTypes(): boolean {
    return this.shareTypes.some((it) => this.hasEvenDividend(it.sharetypeId));
  }

  hasSelectedShareholders(): boolean {
    return this.shareholders.some((it) => this.hasUnevenDividend(it.id));
  }

  toggleEvenDividend(sharetypeId: number) {
    this.validEvenDividendShareTypes = true;
    const dividend = this.shareholderEvenDividends.find((s) => s.id === sharetypeId);
    if (dividend) {
      const index = this.shareholderEvenDividends.indexOf(dividend);
      this.shareholderEvenDividends.splice(index, 1);
    } else {
      const shareType = this.shareTypes.find((s) => s.sharetypeId === sharetypeId);
      if (shareType) {
        const excludeCompanyShares = this.companyOwnsShares(shareType.sharetypeCode)
          ? false
          : undefined;
        this.shareholderEvenDividends.push(
          new EvenDividendModel(
            shareType.sharetypeId,
            shareType.name,
            0,
            false,
            excludeCompanyShares,
          ),
        );
      }
    }
  }

  hasEvenDividend(sharetypeId: number) {
    return this.shareholderEvenDividends.some((s) => s.id === sharetypeId);
  }

  getExistingDividend(shareTypeId: number): EvenDividend | undefined {
    return this.data?.shareholderEvenDividends.find((s) => s.id === shareTypeId);
  }

  evenDividendIsValueTotal(shareTypeId: number) {
    const dividend = this.shareholderEvenDividends.find((s) => s.id === shareTypeId);
    return (
      dividend?.dividendType.value === PaymentMethods.TOTAL_INCL ||
      dividend?.dividendType.value === PaymentMethods.TOTAL_EX
    );
  }

  amountForShareType(shareTypeId: number) {
    return this.shareholderEvenDividends.find((s) => s.id === shareTypeId)!.value;
  }

  hasUnevenDividend(shareholderId: number) {
    return this.shareholderUnevenDividends.some((s) => s.id === shareholderId);
  }

  findUnevenDividendModel(shareholder: ExtendedShareholderDTO, shareType: ShareType) {
    return this.shareholderUnevenDividends
      .find((s) => s.id === shareholder.id)
      ?.shareType.find((st) => st.id === shareType.sharetypeId)?.value;
  }

  shareTypesWithShares(shareholder: ExtendedShareholderDTO) {
    return this.shareTypes.filter((st) => shareholder.calculatedShares[st.sharetypeCode] ?? 0 > 0);
  }

  selectAllUnevenDividendsChecked() {
    for (const s of this.shareholders) {
      if (!this.hasUnevenDividend(s.id)) {
        this.toggleUnevenDividend(s.id);
      }
      for (const st of this.shareTypesWithShares(s)) {
        if (!this.hasUnevenDividendShareType(s.id, st.sharetypeId)) {
          this.toggleShareholderUnevenDividendShareType(s.id, st);
        }
      }
    }
  }

  get unevenDividendInputs() {
    return this.shareholders.map((shareholder) => ({
      shareholderId: shareholder.id,
      shareholderLabel: shareholder.name,
      checked: this.hasUnevenDividend(shareholder.id),
      onChange: () => this.toggleUnevenDividend(shareholder.id),
      disabled: this.isProjectLocked,
      shareTypes: !this.hasUnevenDividend(shareholder.id)
        ? []
        : this.shareTypesWithShares(shareholder).map((shareType) => ({
            sharetypeId: shareType.sharetypeId,
            sharetypeLabel: {
              key: "decisions.dividend.dividendNotEvenlyDivided.receiveOnShareType",
              params: { shareType: shareTypeLabel(shareType) },
            } satisfies TMessage,
            checked: this.hasUnevenDividendShareType(shareholder.id, shareType.sharetypeId),
            onChange: () =>
              this.toggleShareholderUnevenDividendShareType(shareholder.id, shareType),
            disabled: this.isProjectLocked,
            dividend: when(
              this.findUnevenDividendModel(shareholder, shareType),
              (dividendModel) => ({
                inputModel: dividendModel,
                label: {
                  key: "decisions.dividend.total-amount",
                  params: { currency: CURRENCY_NOK },
                } satisfies TMessage,
                prefix: CURRENCY_NOK,
                isLocked: this.isProjectLocked,
              }),
            ),
          })),
    }));
  }

  toggleUnevenDividend(shareholderId: number) {
    this.validUnevenShareholdersSelected = true;
    const dividend = this.shareholderUnevenDividends.find((s) => s.id === shareholderId);
    if (dividend) {
      const index = this.shareholderUnevenDividends.indexOf(dividend);
      this.shareholderUnevenDividends.splice(index, 1);
    } else {
      const shareholder = this.context.shareholderData.shareholders.find(
        (s) => s.id === shareholderId,
      );
      if (shareholder) {
        this.shareholderUnevenDividends.push(
          new UnevenDividendModel(shareholder.id, shareholder.name, []),
        );
      }
    }
  }

  toggleShareholderUnevenDividendShareType(shareholderId: number, sharetype: ShareType) {
    const dividend = this.shareholderUnevenDividends.find((s) => s.id === shareholderId);
    if (dividend) {
      const shareTypeDividend = dividend.shareType.find((s) => s.id === sharetype.sharetypeId);
      if (shareTypeDividend) {
        const index = dividend.shareType.indexOf(shareTypeDividend);
        dividend.shareType.splice(index, 1);
      } else {
        dividend.shareType.push(
          new ShareTypeDividendModel(sharetype.sharetypeId, sharetype.name, undefined),
        );
      }
    }
  }

  hasUnevenDividendShareType(shareholderId: number, sharetypeId: number) {
    const dividend = this.shareholderUnevenDividends.find((s) => s.id === shareholderId);
    return dividend?.shareType.some((s) => s.id === sharetypeId);
  }

  companyOwnsShares(shareTypeCode: ShareTypeCode) {
    const organizationnumber = this.context.companyInfo.company.organizationnumber;
    return this.context.shareholderData.shareholders.some((s) => {
      return (
        s.isCompany &&
        s.organizationNumber === organizationnumber &&
        s.calculatedShares[shareTypeCode]! > 0
      );
    });
  }

  get showPowerOfAttorney() {
    return this.usePowerOfAttorney.value === true;
  }

  setUsePowerOfAttorney(value: boolean) {
    this.usePowerOfAttorney.setValue(value);
    if (this.usePowerOfAttorney.value === false) {
      this.dispatch(addGeneralMeetingDocuments());
    }
  }

  getEvenDividendModel(shareTypeId: number) {
    return this.shareholderEvenDividends.find((s) => s.id === shareTypeId)!.dividendType;
  }

  toggleEvenDividendType(shareTypeId: number, newValue: PaymentMethods) {
    const dividend = this.shareholderEvenDividends.find((s) => s.id === shareTypeId);
    if (dividend) {
      dividend.dividendType.setValue(newValue);
    }
  }
}
